// A month's money (sculpt-api PLAN-payments.md §7, M5): orders paid by card and by transfer, refunds and chargebacks,
// to reconcile with Mollie's settlements and the bank. In one currency at a time (`?currency=`, EUR by default): the
// selector shows when the month had paid orders in more than one (sculpt-api PLAN-multi-currency.md).
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { useAsync } from '../../hooks/useAsync';
import { useQueryParams } from '../../hooks/useQueryParams';
import { money } from '../../services/format';
import { Alert, Field, Filters, Input, PageTitle, Select, Stat } from '../../components/ui';

/** This month as YYYY-MM, in UTC like the API's months. */
export function thisMonth(now = new Date()): string {
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function MonthlyBilling() {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const { get, set, reset } = useQueryParams();
  const month = /^\d{4}-\d{2}$/.test(get('month')) ? get('month') : thisMonth();
  const wanted = /^[A-Z]{3}$/.test(get('currency')) ? get('currency') : '';
  const { data, error, loading } = useAsync(() => api.monthlyBilling(month, wanted || undefined), [month, wanted]);
  const currency = data?.currency ?? (wanted || 'EUR');
  // The one asked for stays listed when another month had none in it, so it can be switched back.
  const currencies = data ? [...new Set([...data.currencies, currency])] : [];
  const amount = (v: string | undefined) => money(v ?? '0', currency, l);
  return (
    <div>
      <PageTitle title={t('monthly.title')} subtitle={t('monthly.subtitle')} />
      <Filters onReset={reset}>
        <Field label={t('monthly.month')}>
          <Input type="month" value={month} onChange={(e) => e.target.value && set({ month: e.target.value })} data-testid="month" />
        </Field>
        {currencies.length > 1 && (
          <Field label={t('monthly.currency')}>
            <Select value={currency} onChange={(e) => set({ currency: e.target.value === 'EUR' ? undefined : e.target.value })} data-testid="monthly-currency">
              {currencies.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </Filters>
      {error && <div className="mb-3"><Alert>{error}</Alert></div>}
      {loading && !data ? (
        <p className="text-[13px] text-faint">{t('common.loading')}</p>
      ) : data && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="monthly">
          <Stat label={t('monthly.paidOrders')} value={data.paid_orders} />
          <Stat label={t('monthly.amount')} value={amount(data.amount)} />
          <Stat label={t('monthly.vat')} value={amount(data.vat)} />
          <Stat label={t('monthly.total')} value={amount(data.total)} />
          <Stat label={t('monthly.byCard')} value={amount(data.by_card)} hint={t('monthly.byCardHint')} />
          <Stat label={t('monthly.byTransfer')} value={amount(data.by_transfer)} />
          <Stat label={t('monthly.refunds', { count: data.refunds })} value={amount(data.refunded)} tone={data.refunds ? 'warn' : undefined} />
          <Stat label={t('monthly.chargebacks', { count: data.chargebacks })} value={amount(data.charged_back)} tone={data.chargebacks ? 'bad' : undefined} />
        </div>
      )}
    </div>
  );
}
